import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { StyledLabel } from '../../../assets/styledComponents/Global';

/** Shared empty-state ring — visible in both light and dark. */
export const emptyStateCircleCss = css`
  box-sizing: border-box;
  width: ${({ $size }) => ($size ? `${$size}px` : '36px')};
  height: ${({ $size }) => ($size ? `${$size}px` : '36px')};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme }) =>
    theme?.dark
      ? theme?.fieldLabel || theme?.surface || '#343B45'
      : theme?.skyblue || '#EFF4FA'};
  border: 2px solid
    ${({ theme }) =>
      theme?.dark
        ? 'rgba(255, 255, 255, 0.4)'
        : theme?.primaryLight || 'rgba(13, 35, 73, 0.12)'};
`;

export const EmptyStateCircle = styled.span`
  ${emptyStateCircleCss}
`;

/**
 * Corporate empty state — no heavy card shadow.
 * Use instead of (or inside) an empty PortletBody.
 */
const EmptyState = ({
  title = 'Nothing to show',
  description = 'There is no content available for this view yet.',
  icon,
  action,
  compact = false,
  className = '',
}) => {
  const theme = useTheme();

  return (
    <Wrap
      className={className}
      $compact={compact}
      role="status"
      aria-live="polite"
    >
      <EmptyStateCircle aria-hidden={!icon} $size={icon ? 40 : 36}>
        {icon || null}
      </EmptyStateCircle>
      <StyledLabel
        size={compact ? '15px' : '16px'}
        weight={600}
        color={
          theme?.dark
            ? theme?.headingColor
            : theme?.primary || theme?.headingColor
        }
        className="mb-1 text-center"
      >
        {title}
      </StyledLabel>
      {description ? (
        <StyledLabel
          size="13px"
          weight={500}
          color={theme?.textMuted || theme?.textColor || '#6F767E'}
          className="text-center mb-0"
        >
          {description}
        </StyledLabel>
      ) : null}
      {action ? <ActionWrap>{action}</ActionWrap> : null}
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  flex: 1 1 auto;
  align-self: stretch;
  min-height: ${({ $compact }) => ($compact ? '120px' : '180px')};
  padding: ${({ $compact }) => ($compact ? '20px 16px' : '32px 20px')};
  background: transparent;
  border: none;
  box-shadow: none;

  ${EmptyStateCircle} {
    margin-bottom: 12px;

    svg {
      max-width: 18px;
      max-height: 18px;
    }
  }
`;

const ActionWrap = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

export default EmptyState;
